import { get, writable, type Writable, type Readable } from 'svelte/store';

export enum NotificationType {
	INFO = 'info',
	FEEDBACK = 'feedback',
	PWA_INSTALL = 'pwa-install',
	HEADSUP = 'headsup',
	AI = 'ai',
}

export type NotificationActionBase = {
	label: string;
};

/**
 * Handle notification action clicks as click event handler
 */
export type NotificationActionClickHandler = {
	handler?: (e: Event) => void;
};

/**
 * Handle notification action clicks as href
 */
export type NotificationActionHref = {
	href?: string;
};

export type NotificationAction = NotificationActionBase & NotificationActionClickHandler & NotificationActionHref;

type NotificationBase = {
	id: string;
	type: NotificationType;
	title: string;
	message: string;
	actions: NotificationAction[];
};

type DismissibleNotification = {
	dismissible: true;
	dismiss: (e: Event) => void;
};

type NonDismissibleNotification = {
	dismissible?: false;
	dismiss?: never;
};

export type Notification = NotificationBase & (DismissibleNotification | NonDismissibleNotification);

export type SubscribableNotification = Readable<Notification>;

export const notifications: Writable<SubscribableNotification[]> = writable([]);

export const addNotification = (newNotificationStore: SubscribableNotification): void => {
	const newNotification: Notification = get(newNotificationStore);
	const currentNotifications: SubscribableNotification[] = get(notifications);
	const alreadyPresent = currentNotifications.some(n => get(n).id === newNotification.id);

	if (!alreadyPresent) {
		notifications.update((current: SubscribableNotification[]) => [...current, newNotificationStore]);
	}
};

export const removeNotification = (id: string): void => {
	notifications.update((currentNotifications: SubscribableNotification[]) =>
		currentNotifications.filter(n => get(n).id !== id)
	);
};
